import { type GetServerSidePropsContext } from 'next';

import { WebCheckoutTemplate } from '../../../templates';

import { checkoutService } from '../../../services/checkout';

import { staticContent } from '../../../data/seja-um-profissional';

import { withEnhancedPageHandler } from '../../../middlewares/withEnhancedPageHandler';
import { parsedBundlesData } from '../../../services/parsedBundlesData';
import { applyCorrelationIdHeader } from '../../../utils/server/applyCorrelationIdHeader';
import {
  MOCK_CONSUMER_WEEK_TRIAL_BUNDLE,
  MOCK_TRIAL_BUNDLE,
} from '../../../mocks/mockBundle';
import {
  featureToggleMercadoPago,
  featureToggleService,
  gamificationActive,
  getGamificationProfiles,
  getPagaleveProfiles,
  increasePriceConfig,
  isBundleTrialConsumerWeekActive,
  isMpCreditCardActive,
  isMpPixActive,
  isPagaleveActive,
  isPagzoopActiveToggle,
  isStarkBankActiveToggle,
  iuguPayments,
  pagarmeToPagaleve,
} from '@/services/featureToggleService';
import WebCheckoutTemplateMercadoPago from '@/templates/WebCheckoutMercadoPago/WebCheckoutTemplateMercadoPago';
import { getUserInfo } from '@/services/getUserInfo';
import { getSavedCardsMP } from '@/services/getSavedCardMP';
import { getUserLocation } from '@/services/getUserLocation';
import { getNewBundlesPrice } from '@/services/getNewBundlesPrice';
import { updateBundlesPrice } from '@/utils/updateBundlePrice';
import { getBundlesTrial } from '@/services/getBundlesTrial';
import { getCards } from '@/services/getCards';
import { detectCardBrand } from '@/utils/detectCardBrand';
import {
  getGamificationInfo,
  stripCupomFromGamificationInfo,
} from '@/services/gamification';
import {
  hasPendingPixInRequest,
  reconcilePendingPixGamificationServer,
} from '@/utils/server/reconcilePendingPixGamificationServer';

function PaymentsPage(props) {
  return <WebCheckoutTemplateMercadoPago {...props} />;
}

const getServerSidePropsHandler = async ({
  query,
  locale,
  req,
  res,
}: GetServerSidePropsContext) => {
  try {
    const { auth_token } = query;

    if (!auth_token) {
      return {
        redirect: {
          destination: '/seja-um-profissional/login',
          permanent: false,
        },
      };
    }

    const customHeaders = applyCorrelationIdHeader(req.headers);

    const { profile, bundles, payment_methods, bundles_trial } =
      await checkoutService({
        authToken: auth_token,
        customHeaders,
        locale,
      });

    const shouldRenderPromotionalBundle = await featureToggleService();

    const isProfileDocumentationBlocked =
      profile.documentation_blocked === true;
    const hasValidationLink = profile.documentation_validate_url != null;

    const shouldGoToValidationFlowInWeb =
      isProfileDocumentationBlocked && hasValidationLink;
    const shouldGoToValidationFlowInApp =
      isProfileDocumentationBlocked && !hasValidationLink;

    if (shouldGoToValidationFlowInWeb) {
      return {
        redirect: {
          destination: profile.documentation_validate_url,
          permanent: false,
        },
      };
    }

    if (shouldGoToValidationFlowInApp) {
      return {
        redirect: {
          destination: `/seja-um-profissional/validacao-de-documentos/manual`,
          permanent: false,
        },
      };
    }

    const userInfo = await getUserInfo(auth_token as string);
    const userId = userInfo.id;
    const userState = userInfo.profile.address.uf;
    const userProfileId = userInfo.profile.id;

    const userHasBundlesTrial = bundles_trial?.length > 0;

    const getTrialBundlesToShow = () => {
      return [...bundles_trial, MOCK_TRIAL_BUNDLE, ...bundles];
    };

    const bundlesArray = userHasBundlesTrial
      ? getTrialBundlesToShow()
      : [...bundles];

    let paymentCards = [];

    const bundlesData = parsedBundlesData(bundlesArray);

    const isIuguActive = true;

    paymentCards = payment_methods.filter((method) => method.type === 'credit');

    const isMercadoPago = await featureToggleMercadoPago();

    const paymentCardsFiltered = [];

    const shouldCreditCardMpBeActive = await isMpCreditCardActive();
    const shouldPixMpBeActive = await isMpPixActive();

    const isPixMpActive = isMercadoPago && shouldPixMpBeActive;
    const isCreditCardMpActive = isMercadoPago && shouldCreditCardMpBeActive;

    if (isCreditCardMpActive) {
      paymentCards = paymentCardsFiltered || [];
    }

    const isIncreasePriceActive = await increasePriceConfig();

    const parseBundle = async () => {
      if (!isIncreasePriceActive) return bundlesData;

      const bundlesIds = bundlesData.map((bundle) => bundle.id);

      if (!userState) return bundlesData;

      const bundlesPriceResult = await getNewBundlesPrice(
        userState,
        bundlesIds
      );

      if (bundlesPriceResult.length === 0) return bundlesData;

      return updateBundlesPrice(bundlesData, bundlesPriceResult);
    };

    const newBundles = await parseBundle();

    const pagaLevePixActive = await isPagaleveActive();
    const pagaLeveProfiles = await getPagaleveProfiles();
    const newDesign = true;

    const isPagaLeveActive = !pagaLevePixActive
      ? pagaLeveProfiles.includes(String(userId))
      : pagaLevePixActive;

    const isPagaLevePixUpFrontActive = await pagarmeToPagaleve();

    const changePagarmeToPagaleve = !isPagaLevePixUpFrontActive
      ? pagaLeveProfiles.includes(String(userId))
      : isPagaLevePixUpFrontActive;

    const isStarkBankActive = await isStarkBankActiveToggle();
    const isPagzoopActive = await isPagzoopActiveToggle();

    const cards = await getCards(userInfo.profile.id);

    const cardsArray = Array.isArray(cards?.cards) ? cards.cards : [];

    const uniqueCards = cardsArray.filter(
      (card, i, arr) =>
        arr.findIndex((c) => c.cardnumber === card.cardnumber) === i
    );

    const parsedCards = uniqueCards.map((card) => ({
      name: card.cardholdername,
      cvv: card.cardcvv,
      number: card.cardnumber,
      validDate: card.cardexpire,
      numberInstallments: 1,
      display: `**** ${card.cardnumber.replace(/\s/g, '').slice(-4)}`,
      brand: detectCardBrand(card.cardnumber),
    }));

    const isGamificationFlagAcitve = await gamificationActive();
    const gamificationProfiles = await getGamificationProfiles();

    const isGamificationActive = !isGamificationFlagAcitve
      ? gamificationProfiles.includes(String(userProfileId))
      : isGamificationFlagAcitve;

    const newUserState = gamificationProfiles.includes(String(userProfileId))
      ? 'MG'
      : userState;

    const gamificationInfo = isGamificationActive
      ? await getGamificationInfo(userProfileId, newUserState)
      : { eligible: false };

    const reconciledGamificationInfo =
      await reconcilePendingPixGamificationServer(req, res, {
        currentProfileId: userProfileId,
        userState: newUserState,
        gamificationInfo,
      });

    const finalGamificationInfo =
      reconciledGamificationInfo || gamificationInfo;
    const stillLocked =
      !reconciledGamificationInfo && hasPendingPixInRequest(req);
    const effectiveGamificationInfo = stillLocked
      ? { ...finalGamificationInfo, descontos: [] }
      : finalGamificationInfo;

    const safeGamificationInfo = stripCupomFromGamificationInfo(
      effectiveGamificationInfo
    );

    return {
      props: {
        staticContent,
        profile,
        bundlesData: newBundles,
        paymentCards: parsedCards,
        authToken: auth_token,
        isCreditCardMpActive,
        isPixMpActive,
        userState: newUserState,
        isPagaLeveActive,
        userInfo,
        changePagarmeToPagaleve,
        newDesign,
        isIuguActive,
        isStarkBankActive,
        isPagzoopActive,
        gamificationInfo: safeGamificationInfo,
        hasLockedPix: stillLocked,
      },
    };
  } catch (err) {
    return {
      redirect: {
        destination: '/seja-um-profissional/login',
        permanent: false,
      },
    };
  }
};

export const getServerSideProps = withEnhancedPageHandler(
  getServerSidePropsHandler
);

export default PaymentsPage;
